import Raven from 'raven-js';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PanelSection } from 'foundations-components/transitional/components/Panel';
import { DEAL_STAGE, FORECAST_CATEGORY } from '../../constants/DealGroupingType';
import { getIsUngatedToForecastingMultipleOutcomes } from '../../selectors/authSelectors';
import { getDealSplitsEnabled } from '../../selectors/portalSettings';
import { sortDealsByDealGroupingOrder, deriveSortedDealGroupingOptions } from '../../utils/dealGroupingUtils';
import { deriveDealSidebarAmountValues, deriveDealSidebarDisplayValues } from '../../utils/dealUtils';
import { DealSidebarCard } from './dealSidebarCard/DealSidebarCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DealSidebarDealsSection = ({
  loading,
  deals,
  forecastCategoryProperty,
  dealStages,
  portalConfig,
  currencyCode,
  isDealStageView,
  owners,
  isTeam
}) => {
  const dealSplitsEnabled = useSelector(getDealSplitsEnabled);
  const isFmoEnabled = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 300,
      showLabel: true,
      size: "md"
    });
  }
  const dealGroupingType = isDealStageView ? DEAL_STAGE : FORECAST_CATEGORY;
  const forecastCategoryOptions = deriveSortedDealGroupingOptions(forecastCategoryProperty.options);
  const dealStageOptions = deriveSortedDealGroupingOptions(dealStages);
  const sortOptionsToUse = isDealStageView ? dealStageOptions : forecastCategoryOptions;
  const dealsSortedByCategoryOrder = sortDealsByDealGroupingOrder(deals, sortOptionsToUse, dealGroupingType);
  const showNoDealsFoundMessage = !deals.size && !loading;
  const DealSplitUniquenessCheckMap = {};
  const computeDealAmount = (deal, dealSplit) => {
    if (!dealSplitsEnabled) {
      return deriveDealSidebarAmountValues(deal, dealSplit, portalConfig, false, isFmoEnabled);
    }
    if (!isFmoEnabled) {
      return deriveDealSidebarAmountValues(deal, dealSplit, portalConfig, true, isFmoEnabled);
    }

    // Case: Both Deal splits & FMO enabled
    return deriveDealSidebarAmountValues(deal, dealSplit, portalConfig, true, isFmoEnabled);
  };
  return /*#__PURE__*/_jsx(PanelSection, {
    display: "flex",
    flex: "1 1 100%",
    flexDirection: "column"
    // Override defaults to prevent whitespace against grey background
    ,
    marginBlockEnd: "0",
    paddingBlockStart: "0",
    paddingInline: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      backgroundColor: "fill-surface-raised",
      flex: "1 1 100%",
      flexDirection: "column",
      gap: "200",
      justifyContent: "start",
      paddingBlock: "600",
      paddingInline: "600",
      children: [dealsSortedByCategoryOrder.map(deal => {
        const {
          categoryLabel,
          dealStageLabel
        } = deriveDealSidebarDisplayValues(deal, forecastCategoryOptions, dealStages);
        const nextStep = deal.properties.hs_next_step ? deal.properties.hs_next_step.value : undefined;

        // For deals with multiple splits (this is possible when viewing for teams where multiple team members are split on the same deal)
        // We render multiple cards each with unique split value
        return deal.dealSplits.map(dealSplit => {
          var _deal$properties;
          const {
            amount,
            userId
          } = computeDealAmount(deal, dealSplit);
          const key = `${deal.dealId}/${userId}`;

          // There shouldn't ever be 2 splits with the same user, but we have seen this happen
          // in PROD via JIRA. If it does report it. This can be removed once the scope of this
          // issue is discovered and we can patch it.
          // https://issues.hubspotcentral.com/browse/RA-20663
          if (DealSplitUniquenessCheckMap[key]) {
            Raven.captureMessage('Duplicate user in deal splits', {
              extra: {
                deal
              }
            });
          } else {
            DealSplitUniquenessCheckMap[key] = true;
          }
          return /*#__PURE__*/_jsx(DealSidebarCard, {
            amount: amount,
            categoryLabel: categoryLabel,
            currencyCode: currencyCode,
            deal: deal,
            dealStageLabel: dealStageLabel,
            email: isTeam && owners.getIn([Number(userId), 'email']),
            isDealSplittingEnabled: !!dealSplitsEnabled,
            id: deal.dealId,
            name: deal === null || deal === void 0 || (_deal$properties = deal.properties) === null || _deal$properties === void 0 || (_deal$properties = _deal$properties.dealname) === null || _deal$properties === void 0 ? void 0 : _deal$properties.value,
            nextStep: nextStep,
            ownerName: isTeam ? formatName({
              firstName: owners.getIn([Number(userId), 'firstName']),
              lastName: owners.getIn([Number(userId), 'lastName'])
            }) : undefined
          }, key);
        });
      }), showNoDealsFoundMessage && /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "no-deals-found-message",
        message: "dealSidebar.noDealsFoundMessage"
      })]
    })
  });
};